import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useWebsocketEndpoints, useCreateWebsocketEndpoint, useUpdateWebsocketEndpoint, useDeleteWebsocketEndpoint } from "@/hooks/use-websocket-endpoints";
import type { WebsocketEndpoint } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<WebsocketEndpoint>[] = [
  { key: "name", header: "Name" },
  { key: "uri", header: "URI", render: (w) => <span className="font-mono text-xs truncate max-w-[200px] block">{w.uri}</span> },
  { key: "protocols", header: "Protocols" },
  { key: "connection_type", header: "Conn Type" },
];

export default function WebsocketEndpointsPage() {
  const { data, isLoading, error, refetch, isFetching } = useWebsocketEndpoints();
  const createMut = useCreateWebsocketEndpoint();
  const updateMut = useUpdateWebsocketEndpoint();
  const deleteMut = useDeleteWebsocketEndpoint();

  const defaultWs = {
    name: "",
    uri: "",
    protocols: "ari",
    connection_type: "per_call_config",
    connection_timeout: 500,
    reconnect_interval: 500,
    reconnect_attempts: 5,
    tls_enabled: false,
    ca_list_file: null as string | null,
    ca_list_path: null as string | null,
    cert_file: null as string | null,
    priv_key_file: null as string | null,
    verify_server_cert: null as boolean | null,
    verify_server_hostname: null as boolean | null,
    headers: null as Record<string, string> | null,
    description: null as string | null,
  };

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WebsocketEndpoint | null>(null);
  const [form, setForm] = useState(defaultWs);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<WebsocketEndpoint | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((w) => [w.name, w.uri].some((v) => v?.toLowerCase().includes(q)));
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultWs); setOpen(true); };
  const openEdit = (w: WebsocketEndpoint) => {
    setEditing(w);
    setForm({
      name: w.name,
      uri: w.uri,
      protocols: w.protocols,
      connection_type: w.connection_type,
      connection_timeout: w.connection_timeout,
      reconnect_interval: w.reconnect_interval,
      reconnect_attempts: w.reconnect_attempts,
      tls_enabled: w.tls_enabled,
      ca_list_file: w.ca_list_file ?? null,
      ca_list_path: w.ca_list_path ?? null,
      cert_file: w.cert_file ?? null,
      priv_key_file: w.priv_key_file ?? null,
      verify_server_cert: w.verify_server_cert ?? null,
      verify_server_hostname: w.verify_server_hostname ?? null,
      headers: w.headers ?? null,
      description: w.description ?? null,
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("WebSocket endpoint updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("WebSocket endpoint created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <PageHeader title="WebSocket Endpoints" description="chan_websocket client connection configurations" />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search endpoints..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create WS Endpoint</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(w) => w.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No WebSocket endpoints configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit WebSocket Endpoint" : "Create WebSocket Endpoint"}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </>
        }
      >
        <div className="space-y-6">
          <FormSection title="Connection" columns={2}>
            <Field label="Name" required htmlFor="name" info="WebSocket client connection name. Referenced by Dial (WebSocket) actions. Example: stt-service, ai-bot.">
              <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
            </Field>
            <Field label="URI" required htmlFor="uri" className="sm:col-span-2" info="WebSocket server URL to connect to. Example: wss://my-stt-service.example.com/v1/stream. Use ws:// for unencrypted, wss:// for TLS.">
              <Input id="uri" value={form.uri} onChange={(e) => updateField("uri", e.target.value)} placeholder="wss://example.com/ari/events" />
            </Field>
            <Field label="Protocols" htmlFor="protocols" info="WebSocket sub-protocols to negotiate. ari for Asterisk ARI WebSocket. Custom services may use other values.">
              <Input id="protocols" value={form.protocols} onChange={(e) => updateField("protocols", e.target.value)} />
            </Field>
            <Field label="Connection Type" htmlFor="connection_type" info="per_call_config - a new connection per call (what chan_websocket media legs use). persistent - one long-lived connection shared by all calls. These are the only two values res_websocket_client accepts.">
              <Select id="connection_type" value={form.connection_type} onChange={(e) => updateField("connection_type", e.target.value)}>
                <option value="per_call_config">Per Call Config</option>
                <option value="persistent">Persistent</option>
              </Select>
            </Field>
          </FormSection>

          <FormSection title="Reconnect" columns={3}>
            <Field label="Connection Timeout (ms)" htmlFor="connection_timeout" info="Milliseconds to wait for the WebSocket connection to establish. Default 500. Increase for slow networks or a TLS handshake over the public internet.">
              <Input type="number" id="connection_timeout" value={form.connection_timeout} onChange={(e) => updateField("connection_timeout", Number(e.target.value))} />
            </Field>
            <Field label="Reconnect Interval (ms)" htmlFor="reconnect_interval" info="Milliseconds between reconnection attempts after a failure. Default 500.">
              <Input type="number" id="reconnect_interval" value={form.reconnect_interval} onChange={(e) => updateField("reconnect_interval", Number(e.target.value))} />
            </Field>
            <Field label="Reconnect Attempts" htmlFor="reconnect_attempts" info="Maximum reconnection attempts before giving up. Default 5. 0 = unlimited retries.">
              <Input type="number" id="reconnect_attempts" value={form.reconnect_attempts} onChange={(e) => updateField("reconnect_attempts", Number(e.target.value))} />
            </Field>
          </FormSection>

          <FormSection title="Options" columns={1}>
            <Switch id="tls_enabled" checked={form.tls_enabled} onChange={(e) => updateField("tls_enabled", e.target.checked)} label={<span>TLS Enabled <HelpIcon text="Enable TLS for the WebSocket connection. Must match the URI scheme (wss:// needs TLS)." /></span>} />
            <Field label="Description" htmlFor="description" info="Human-readable note about this WebSocket connection purpose.">
              <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
            </Field>
          </FormSection>

          {form.tls_enabled && (
            <FormSection title="TLS" columns={2}>
              <Field label="CA List File" htmlFor="ca_list_file" className="sm:col-span-2" info="File holding the CA certificates used to verify the server. Asterisk has NO default trust store here — leave this empty with a public wss:// server and the handshake fails with 'unable to get local issuer certificate'. For a public CA (e.g. an AWS ALB), use /etc/ssl/certs/ca-certificates.crt.">
                <Input id="ca_list_file" value={form.ca_list_file ?? ""} onChange={(e) => updateField("ca_list_file", e.target.value || null)} placeholder="/etc/ssl/certs/ca-certificates.crt" />
              </Field>
              <Field label="CA List Path" htmlFor="ca_list_path" info="Directory of hashed CA certificates, as an alternative to CA List File. Example: /etc/ssl/certs.">
                <Input id="ca_list_path" value={form.ca_list_path ?? ""} onChange={(e) => updateField("ca_list_path", e.target.value || null)} />
              </Field>
              <Field label="Client Cert File" htmlFor="cert_file" info="Client certificate, only if the server requires mutual TLS.">
                <Input id="cert_file" value={form.cert_file ?? ""} onChange={(e) => updateField("cert_file", e.target.value || null)} />
              </Field>
              <Field label="Client Private Key" htmlFor="priv_key_file" info="Private key matching the client certificate, for mutual TLS.">
                <Input id="priv_key_file" value={form.priv_key_file ?? ""} onChange={(e) => updateField("priv_key_file", e.target.value || null)} />
              </Field>
              <Field label="Verify Server Cert" htmlFor="verify_server_cert" info="Verify the server's certificate chain. Leave as Asterisk default unless you need to override. Turning this off accepts any certificate — use only against a server you control.">
                <Select id="verify_server_cert" value={form.verify_server_cert === null ? "" : String(form.verify_server_cert)} onChange={(e) => updateField("verify_server_cert", e.target.value === "" ? null : e.target.value === "true")}>
                  <option value="">Asterisk default</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </Field>
              <Field label="Verify Server Hostname" htmlFor="verify_server_hostname" info="Verify the server hostname matches the certificate. Asterisk checks the common name ONLY and ignores Subject Alternative Names, so a wildcard cert that legitimately covers the host via a SAN is still rejected. Set No in that case and keep Verify Server Cert on — the chain is still verified.">
                <Select id="verify_server_hostname" value={form.verify_server_hostname === null ? "" : String(form.verify_server_hostname)} onChange={(e) => updateField("verify_server_hostname", e.target.value === "" ? null : e.target.value === "true")}>
                  <option value="">Asterisk default</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </Field>
            </FormSection>
          )}
        </div>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete WebSocket endpoint"
        description={<>Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("WebSocket endpoint deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
